import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ProfileSection } from "@/lib/api/profile-sections";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { Upload, X, AlertCircle } from "lucide-react";
import { useRef, useState } from "react";

interface ProfileSectionFormProps {
  section?: ProfileSection;
  onSubmit: (data: {
    title: string;
    description: string;
    file?: File;
    removeImage?: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ProfileSectionForm({
  section,
  onSubmit,
  onCancel,
  isLoading = false,
}: ProfileSectionFormProps) {
  const [title, setTitle] = useState(section?.title || "");
  const [description, setDescription] = useState(section?.description || "");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get signed URL for existing image
  const { url: existingImageUrl } = useSignedMedia(section?.image_url);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Clear previous errors
      setError(null);

      // Validate file size (10MB max)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }

      // Validate file type
      if (!selectedFile.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      setFile(selectedFile);
      setRemoveExistingImage(false); // Reset remove flag when new file is selected

      // Create preview URL
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveExistingImage = () => {
    setRemoveExistingImage(true);
    handleRemoveFile();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim() || !description.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    if (title.length > 100) {
      setError("Title must be 100 characters or less");
      return;
    }

    if (description.length > 1000) {
      setError("Description must be 1000 characters or less");
      return;
    }

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        file: file || undefined,
        removeImage: removeExistingImage,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An error occurred while saving",
      );
    }
  };

  // Determine what image to show
  const imageToShow = previewUrl || existingImageUrl;
  const hasImage = !!imageToShow;
  const showRemovalMessage =
    removeExistingImage && !previewUrl && existingImageUrl;

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div>
          <Label htmlFor="title" className="text-sm font-medium">
            Title <span className="text-red-500">*</span>
          </Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter section title"
            maxLength={100}
            className="mt-1"
          />
          <p className="text-muted-foreground mt-1 text-xs">
            {title.length}/100 characters
          </p>
        </div>

        <div>
          <Label htmlFor="description" className="text-sm font-medium">
            Description <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter section description"
            maxLength={1000}
            className="mt-1 min-h-[100px] resize-none"
          />
          <p className="text-muted-foreground mt-1 text-xs">
            {description.length}/1000 characters
          </p>
        </div>

        <div>
          <Label className="text-sm font-medium">Image (Optional)</Label>
          <div className="mt-1">
            {hasImage ? (
              <div className="space-y-3">
                <div className="group relative flex items-center justify-center">
                  <img
                    src={imageToShow}
                    alt="Section preview"
                    className={`h-48 w-full max-w-md rounded-lg border object-cover ${
                      removeExistingImage ? "opacity-50" : ""
                    }`}
                  />
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-colors group-hover:bg-black/20">
                    <div className="flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white/90 text-black hover:bg-white"
                      >
                        Change Image
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={
                          file ? handleRemoveFile : handleRemoveExistingImage
                        }
                        className="bg-red-500/90 hover:bg-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                {file && (
                  <div className="text-muted-foreground bg-muted rounded p-2 text-xs">
                    <strong>New image selected:</strong> {file.name} (
                    {(file.size / (1024 * 1024)).toFixed(2)} MB)
                  </div>
                )}
                {showRemovalMessage && (
                  <div className="text-muted-foreground bg-muted rounded p-2 text-xs">
                    <strong>Image will be removed</strong> when you save the
                    section
                  </div>
                )}
              </div>
            ) : (
              <div
                className="border-muted-foreground/25 hover:border-muted-foreground/50 cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
                <p className="text-muted-foreground text-sm">
                  Click to upload an image
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Maximum file size: 10MB
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? "Saving..."
              : section
                ? "Update Section"
                : "Add Section"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
